import { X, CheckCircle, AlertCircle } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface WorkCodeTranslation {
  work_code_id: string;
  locale: string;
  translated_code: string;
  translated_description: string;
}

interface WorkCode {
  id?: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
}

interface FailureCode {
  id?: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
}

interface ControllerData {
  id: string;
  name: string;
  controller_number: number;
  location: string;
  photo_url: string | null;
  zones: Array<{
    id: string;
    zone_number: number;
    type: string;
    location: string;
  }>;
}

interface DoubleCheckData {
  id: string;
  description: string;
  size: number;
  photo_url: string | null;
}

interface ControllerInspection {
  controller_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
}

interface DoubleCheckInspection {
  doublecheck_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
}

interface ZoneIssueEntry {
  tempId: string;
  work_code: string;
  notes: string;
  photo_urls: string[];
}

interface ZoneInspection {
  zone_id: string;
  issues: ZoneIssueEntry[];
}

interface InspectionSummaryModalProps {
  jobSiteName: string;
  controllers: ControllerData[];
  controllerInspections: ControllerInspection[];
  controllerFailureCodes: FailureCode[];
  doubleChecks: DoubleCheckData[];
  doublecheckInspections: DoubleCheckInspection[];
  doublecheckFailureCodes: FailureCode[];
  zoneInspections: ZoneInspection[];
  workCodes: WorkCode[];
  translations?: WorkCodeTranslation[];
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function InspectionSummaryModal({
  jobSiteName,
  controllers,
  controllerInspections,
  controllerFailureCodes,
  doubleChecks,
  doublecheckInspections,
  doublecheckFailureCodes,
  zoneInspections,
  workCodes,
  translations = [],
  onConfirm,
  onCancel,
  isSubmitting,
}: InspectionSummaryModalProps) {
  const { t } = useTranslation('inspection');
  const totalControllers = controllers.length;
  const totalDoubleChecks = doubleChecks.length;
  const totalZones = controllers.reduce((sum, c) => sum + c.zones.length, 0);

  const controllersWithIssues = controllerInspections.filter(ci =>
    ci.failure_codes.some(code => {
      const fc = controllerFailureCodes.find(f => f.code === code);
      return fc?.is_issue;
    })
  ).length;

  const doublechecksWithIssues = doublecheckInspections.filter(di =>
    di.failure_codes.some(code => {
      const fc = doublecheckFailureCodes.find(f => f.code === code);
      return fc?.is_issue;
    })
  ).length;

  const zonesWithIssues = zoneInspections.filter(zi =>
    zi.issues.some(issue => {
      const wc = workCodes.find(w => w.code === issue.work_code);
      return wc?.is_issue;
    })
  ).length;

  const totalPhotos =
    controllerInspections.reduce((sum, ci) => sum + (ci.photo_urls?.length || 0), 0) +
    doublecheckInspections.reduce((sum, di) => sum + di.photo_urls.length, 0) +
    zoneInspections.reduce((sum, zi) => sum + zi.issues.reduce((s, i) => s + i.photo_urls.length, 0), 0);

  // Validation: Check for uninspected items
  const inspectedControllerIds = new Set(
    controllerInspections
      .filter(ci => ci.failure_codes && ci.failure_codes.length > 0)
      .map(ci => ci.controller_id)
  );
  const uninspectedControllers = controllers.filter(c => !inspectedControllerIds.has(c.id));

  const inspectedDoubleCheckIds = new Set(
    doublecheckInspections
      .filter(di => di.failure_codes && di.failure_codes.length > 0)
      .map(di => di.doublecheck_id)
  );
  const uninspectedDoubleChecks = doubleChecks.filter(dc => !inspectedDoubleCheckIds.has(dc.id));

  const allZones = controllers.flatMap(c => c.zones.map(z => ({ ...z, controllerId: c.id })));
  const inspectedZoneIds = new Set(
    zoneInspections
      .filter(zi => zi.issues && zi.issues.length > 0)
      .map(zi => zi.zone_id)
  );
  const uninspectedZones = allZones.filter(z => !inspectedZoneIds.has(z.id));

  const hasUninspectedItems = uninspectedControllers.length > 0 ||
                              uninspectedDoubleChecks.length > 0 ||
                              uninspectedZones.length > 0;

  const getTranslatedFailureCode = (code: string, codes: FailureCode[]) => {
    if (translations.length === 0) return code;
    const fc = codes.find(f => f.code === code);
    if (!fc?.id) return code;
    const tr = translations.find(x => x.work_code_id === fc.id);
    return tr?.translated_code || code;
  };

  const getTranslatedWorkCode = (code: string) => {
    if (translations.length === 0) return code;
    const wc = workCodes.find(w => w.code === code);
    if (!wc?.id) return code;
    const tr = translations.find(x => x.work_code_id === wc.id);
    return tr?.translated_code || code;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-surface rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8">
        <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle size={28} />
            <h2 className="text-xl font-bold">{t('summary.title')}</h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-1 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="bg-gradient-to-r from-green-50 to-slate-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-theme-primary mb-4">{t('summary.reportInformation')}</h3>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <p className="text-sm text-slate-600 font-medium">{t('summary.propertyLabel')}</p>
                <p className="text-base text-slate-900">{jobSiteName}</p>
              </div>
            </div>
          </div>

          {hasUninspectedItems && (
            <div className="bg-red-50 dark:bg-red-500/10 border-2 border-red-300 dark:border-red-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={24} />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-800 dark:text-red-300 mb-2">{t('summary.incompleteTitle')}</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                    {t('summary.incompleteMessage')}
                  </p>
                  <div className="space-y-2">
                    {uninspectedControllers.length > 0 && (
                      <div className="bg-white rounded p-3">{/* theme-allow: white card stays light on red-50 alert panel */}
                        <p className="text-sm font-semibold text-red-800 mb-1">
                          {t('summary.uninspectedControllers', { n: uninspectedControllers.length })}
                        </p>
                        <div className="text-xs text-red-800 space-y-1">
                          {uninspectedControllers.map(c => (
                            <div key={c.id}>
                              Controller #{c.controller_number} - {c.location}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {uninspectedDoubleChecks.length > 0 && (
                      <div className="bg-white rounded p-3">{/* theme-allow: white card stays light on red-50 alert panel */}
                        <p className="text-sm font-semibold text-red-800 mb-1">
                          {t('summary.uninspectedDoubleChecks', { n: uninspectedDoubleChecks.length })}
                        </p>
                        <div className="text-xs text-red-800 space-y-1">
                          {uninspectedDoubleChecks.map(dc => (
                            <div key={dc.id}>
                              {dc.size}" - {dc.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {uninspectedZones.length > 0 && (
                      <div className="bg-white rounded p-3">{/* theme-allow: white card stays light on red-50 alert panel */}
                        <p className="text-sm font-semibold text-red-800 mb-1">
                          {t('summary.uninspectedZones', { n: uninspectedZones.length })}
                        </p>
                        <div className="text-xs text-red-800 space-y-1 max-h-32 overflow-y-auto">
                          {uninspectedZones.map(z => {
                            const controller = controllers.find(c => c.id === z.controllerId);
                            return (
                              <div key={z.id}>
                                Controller #{controller?.controller_number}, Zone #{z.zone_number} - {z.location}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-surface border-2 border-blue-200 rounded-lg p-4">
              <p className="text-sm text-theme-secondary font-medium">{t('summary.tileControllers')}</p>
              <p className="text-2xl font-bold text-blue-600">{totalControllers}</p>
              {controllersWithIssues > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t('summary.withIssues', { count: controllersWithIssues })}</p>
              )}
            </div>
            <div className="bg-surface border-2 border-teal-200 rounded-lg p-4">
              <p className="text-sm text-theme-secondary font-medium">{t('summary.tileDoubleChecks')}</p>
              <p className="text-2xl font-bold text-teal-600">{totalDoubleChecks}</p>
              {doublechecksWithIssues > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t('summary.withIssues', { count: doublechecksWithIssues })}</p>
              )}
            </div>
            <div className="bg-surface border-2 border-green-200 rounded-lg p-4">
              <p className="text-sm text-theme-secondary font-medium">{t('summary.tileZones')}</p>
              <p className="text-2xl font-bold text-green-600">{totalZones}</p>
              {zonesWithIssues > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t('summary.withIssues', { count: zonesWithIssues })}</p>
              )}
            </div>
            <div className="bg-surface border-2 border-orange-200 rounded-lg p-4">
              <p className="text-sm text-theme-secondary font-medium">{t('summary.tilePhotos')}</p>
              <p className="text-2xl font-bold text-orange-600">{totalPhotos}</p>
            </div>
          </div>

          <div className="space-y-6">
            {controllersWithIssues > 0 && (
              <div>
                <h3 className="text-lg font-bold text-theme-primary mb-3">{t('summary.issuesSectionControllers')}</h3>
                {controllerInspections
                  .filter(ci => ci.failure_codes.some(code => {
                    const fc = controllerFailureCodes.find(f => f.code === code);
                    return fc?.is_issue;
                  }))
                  .map(inspection => {
                    const controller = controllers.find(c => c.id === inspection.controller_id);
                    if (!controller) return null;

                    return (
                      <div key={controller.id} className="bg-surface border border-line rounded-lg p-4 mb-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-semibold text-theme-primary">
                              Controller #{controller.controller_number}
                              {controller.name && ` - ${controller.name}`}
                            </h4>
                            <p className="text-sm text-theme-secondary mb-2">{controller.location}</p>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {inspection.failure_codes
                                .filter(code => {
                                  const fc = controllerFailureCodes.find(f => f.code === code);
                                  return fc?.is_issue;
                                })
                                .map(code => (
                                  <span
                                    key={code}
                                    className="inline-block px-2 py-1 bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 text-xs font-semibold rounded"
                                  >
                                    {getTranslatedFailureCode(code, controllerFailureCodes)}
                                  </span>
                                ))}
                            </div>
                            {inspection.notes && (
                              <div className="bg-blue-50 dark:bg-blue-500/10 border-l-2 border-blue-400 dark:border-blue-500/30 px-3 py-2 rounded">
                                <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">{t('summary.notesLabel')}</p>
                                <p className="text-sm text-blue-800 dark:text-blue-300">{inspection.notes}</p>
                              </div>
                            )}
                            {inspection.photo_urls && inspection.photo_urls.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-semibold text-orange-800 dark:text-orange-300 mb-2">{t('summary.photosLabel')}</p>
                                <div className="flex flex-wrap gap-2">
                                  {inspection.photo_urls.map((url, idx) => (
                                    <div key={idx} className="relative group">
                                      <img
                                        src={url}
                                        alt={t('photo.altPhoto', { n: idx + 1 })}
                                        className="w-20 h-20 object-cover rounded-lg border-2 border-orange-200 cursor-pointer hover:border-orange-400 transition-all"
                                        onClick={() => window.open(url, '_blank')}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {doublechecksWithIssues > 0 && (
              <div>
                <h3 className="text-lg font-bold text-theme-primary mb-3">{t('summary.issuesSectionDoubleChecks')}</h3>
                {doublecheckInspections
                  .filter(di => di.failure_codes.some(code => {
                    const fc = doublecheckFailureCodes.find(f => f.code === code);
                    return fc?.is_issue;
                  }))
                  .map(inspection => {
                    const doubleCheck = doubleChecks.find(dc => dc.id === inspection.doublecheck_id);
                    if (!doubleCheck) return null;

                    return (
                      <div key={doubleCheck.id} className="bg-surface border border-line rounded-lg p-4 mb-3">
                        <h4 className="font-semibold text-theme-primary mb-2">
                          {doubleCheck.size}" Double Check - {doubleCheck.description}
                        </h4>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {inspection.failure_codes
                            .filter(code => {
                              const fc = doublecheckFailureCodes.find(f => f.code === code);
                              return fc?.is_issue;
                            })
                            .map(code => (
                              <span
                                key={code}
                                className="inline-block px-2 py-1 bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 text-xs font-semibold rounded"
                              >
                                {getTranslatedFailureCode(code, doublecheckFailureCodes)}
                              </span>
                            ))}
                        </div>
                        {inspection.notes && (
                          <div className="bg-blue-50 dark:bg-blue-500/10 border-l-2 border-blue-400 dark:border-blue-500/30 px-3 py-2 rounded mb-2">
                            <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">{t('summary.notesLabel')}</p>
                            <p className="text-sm text-blue-800 dark:text-blue-300">{inspection.notes}</p>
                          </div>
                        )}
                        {inspection.photo_urls.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-orange-800 dark:text-orange-300 mb-2">{t('summary.photosLabel')}</p>
                            <div className="flex flex-wrap gap-2">
                              {inspection.photo_urls.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img
                                    src={url}
                                    alt={t('photo.altPhoto', { n: idx + 1 })}
                                    className="w-20 h-20 object-cover rounded-lg border-2 border-orange-200 cursor-pointer hover:border-orange-400 transition-all"
                                    onClick={() => window.open(url, '_blank')}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {zonesWithIssues > 0 && (
              <div>
                <h3 className="text-lg font-bold text-theme-primary mb-3">{t('summary.issuesSectionZones')}</h3>
                {zoneInspections
                  .filter(zi => zi.issues.some(issue => {
                    const wc = workCodes.find(w => w.code === issue.work_code);
                    return wc?.is_issue;
                  }))
                  .map(inspection => {
                    const zone = controllers.flatMap(c => c.zones).find(z => z.id === inspection.zone_id);
                    if (!zone) return null;

                    const controller = controllers.find(c => c.zones.some(z => z.id === inspection.zone_id));
                    if (!controller) return null;

                    const issueEntries = inspection.issues.filter(issue => {
                      const wc = workCodes.find(w => w.code === issue.work_code);
                      return wc?.is_issue;
                    });

                    return (
                      <div key={zone.id} className="bg-surface border border-line rounded-lg p-4 mb-3">
                        <h4 className="font-semibold text-theme-primary mb-2">
                          Controller #{controller.controller_number}, Zone #{zone.zone_number} ({zone.type})
                        </h4>
                        <p className="text-sm text-theme-secondary mb-2">{zone.location}</p>
                        <div className="space-y-3">
                          {issueEntries.map((issue, idx) => (
                            <div key={idx} className="border border-amber-200 dark:border-amber-500/30 rounded-lg p-3 bg-amber-50 dark:bg-amber-500/10">
                              <span className="inline-block px-2 py-1 bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 text-xs font-semibold rounded mb-2">
                                {getTranslatedWorkCode(issue.work_code)}
                              </span>
                              {issue.notes && (
                                <div className="bg-white border-l-2 border-blue-400 px-3 py-2 rounded mb-2">{/* theme-allow: white note card stays light on amber-50 zone panel */}
                                  <p className="text-xs font-semibold text-blue-800 mb-1">{t('summary.notesLabel')}</p>
                                  <p className="text-sm text-blue-800">{issue.notes}</p>
                                </div>
                              )}
                              {issue.photo_urls.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-orange-800 dark:text-orange-300 mb-2">{t('summary.photosLabel')}</p>
                                  <div className="flex flex-wrap gap-2">
                                    {issue.photo_urls.map((url, pIdx) => (
                                      <div key={pIdx} className="relative group">
                                        <img
                                          src={url}
                                          alt={t('photo.altPhoto', { n: pIdx + 1 })}
                                          className="w-20 h-20 object-cover rounded-lg border-2 border-orange-200 cursor-pointer hover:border-orange-400 transition-all"
                                          onClick={() => window.open(url, '_blank')}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        <div className="bg-subtle px-6 py-4 border-t border-line flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-surface border border-line text-theme-primary rounded-lg hover:bg-subtle font-semibold transition-colors disabled:opacity-50"
          >
            {t('summary.backToEdit')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting || hasUninspectedItems}
            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            title={hasUninspectedItems ? t('summary.tooltipMustInspect') : ''}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('summary.submitting')}
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                {t('summary.confirmSubmit')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
